import { useState } from 'react'
import { useNavigate, useMatchRoute } from '@tanstack/react-router'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { FormattedMessage } from 'react-intl'
import { useAtom, useAtomValue } from 'jotai'
import { globalLoadingAtom } from '@/state/global'
import { loggedInEmailAtom } from '@/state/authentication/login'
import {
  selectedDashboardInfoAtom,
  selectedWorkOrderAtom,
} from '@/state/dashboard'
import { clearSessionState } from '@/state/clearSessionState'
import { getUserInitials } from './helpers'
import { useDialog } from '@/state/dialog/hooks'
import { DialogType } from '@/state/dialog/dialogTypes'
import { logout } from '@/services/authentication/logout'
import { logUserAction } from '@/services/dashboard/logUserAction'
import { UserActivityAction } from '@/types/userActivity'
import type { UserActivityLogPayload } from '@/types/userActivity'
import { TestIds } from '@/constants/testIds'

export function HeaderDropdown() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [, setGlobalLoadingState] = useAtom(globalLoadingAtom)
  const loggedInEmail = useAtomValue(loggedInEmailAtom)
  const selectedDashboardInfo = useAtomValue(selectedDashboardInfoAtom)
  const selectedWorkOrder = useAtomValue(selectedWorkOrderAtom)
  const matchRoute = useMatchRoute()
  const navigate = useNavigate()
  const { openDialog } = useDialog()

  const userInitials = getUserInitials(selectedDashboardInfo)
  const hideLogoutLink = matchRoute({ to: '/readOnly' })

  const handleToggleDropdown = () => setIsDropdownOpen((open) => !open)

  const handleAccountInfoClick = () => {
    const workOrderId =
      selectedWorkOrder?.workOrderId ?? selectedDashboardInfo?.workOrderId
    const activityPayload: UserActivityLogPayload = {
      WorkOrderId: workOrderId?.toString(),
      UserId: loggedInEmail ?? selectedWorkOrder?.ownerEmail,
      Action: UserActivityAction.AccountInfoSelection,
    }
    logUserAction(activityPayload)
    openDialog(DialogType.ACCOUNT_INFO_MODAL)
    setIsDropdownOpen(false)
  }

  const handleLogoutClick = async () => {
    setGlobalLoadingState(true)
    clearSessionState()
    setIsDropdownOpen(false)
    try {
      await logout()
    } catch {
      // Local state is already cleared, so logout can complete if the API is unavailable.
    } finally {
      setGlobalLoadingState(false)
      navigate({ to: '/login', replace: true })
    }
  }

  return (
    <div className="relative">
      <div
        data-testid={TestIds.header.dropdownToggle}
        className="flex items-center gap-2 cursor-pointer"
        onClick={handleToggleDropdown}
        aria-haspopup="true"
        aria-expanded={isDropdownOpen}
        aria-label="User menu"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleToggleDropdown()
        }}
        role="button"
      >
        <div
          data-testid={TestIds.header.userInitials}
          className="w-10 h-10 rounded-full bg-white border-2 border-gray-500 flex items-center justify-center text-gray-700 font-semibold"
        >
          {userInitials}
        </div>
        <ChevronDownIcon className="w-5 h-5 text-red-600" />
      </div>

      {isDropdownOpen && (
        <div
          data-testid={TestIds.header.dropdownMenu}
          className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50"
        >
          <button
            data-testid={TestIds.header.accountInfoButton}
            className="block w-full text-left px-4 py-2 !text-black hover:bg-gray-100"
            onClick={handleAccountInfoClick}
            aria-label="Account Info"
          >
            <FormattedMessage id="headerFooter.accountInfo" />
          </button>
          {!hideLogoutLink && (
            <button
              data-testid={TestIds.header.logoutButton}
              className="block w-full text-left px-4 py-2 !text-black hover:bg-gray-100"
              onClick={handleLogoutClick}
              aria-label="Logout"
            >
              <FormattedMessage id="headerFooter.logout" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
